<template>
  <div class="relative" @keydown.esc="hideLinkMenu">
    <EditorMenuBar :editor="editor">
      <div
        slot-scope="{ commands, isActive, focused, getMarkAttrs }"
        class="menubar"
        :class="{ 'is-focused': focused }"
      >
        <button
          v-if="showBold"
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >
          <span class="menu-icon-text font-black">b</span>
        </button>

        <button
          v-if="showItalic"
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >
          <span class="menu-icon-text italic">i</span>
        </button>

        <button
          v-if="showHeading"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >
          <span class="menu-icon-text">h1</span>
        </button>

        <button
          v-if="showHeading"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >
          <span class="menu-icon-text">h2</span>
        </button>

        <button
          v-if="showHeading"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >
          <span class="menu-icon-text">h3</span>
        </button>

        <button
          v-if="showParagraph"
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
        >
          <span class="menu-icon-text">p</span>
        </button>

        <button
          v-if="showLink"
          class="menubar__button"
          :class="{ 'is-active': isActive.link() }"
          @click="showLinkMenu(getMarkAttrs('link'))"
        >
          <v-icon name="link" scale="0.76" class="menu-icon" />
        </button>

        <div class="link-menu" :class="{ 'is-active': linkMenuIsActive }">
          <form
            class="link-menu-form"
            @submit.prevent="setLinkUrl(commands.link, linkUrl)"
          >
            <input
              ref="linkInput"
              v-model="linkUrl"
              class="link-menu-input"
              type="text"
              placeholder="https://"
              @keydown.esc="hideLinkMenu"
            />
            <button
              class="link-menu-button"
              type="button"
              title="Remove link"
              @click="setLinkUrl(commands.link, null)"
            >
              <v-icon name="times" />
            </button>
          </form>
        </div>
      </div>
    </EditorMenuBar>

    <Loader v-if="loading" label="" class="loading-icon" />

    <EditorContent
      :editable="disabled || loading"
      :editor="editor"
      class="input"
      :class="{ disabled: disabled, loading }"
    />
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import { Bold, Italic, Link, HardBreak, Heading } from "tiptap-extensions";
import Loader from "@/components/UI/Loader";

export default {
  name: "RichInput",
  components: {
    EditorContent,
    EditorMenuBar,
    Loader,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      default: 0,
      required: false,
      type: Number,
    },
    multiLine: {
      type: Boolean,
      default: true,
    },
    showBold: {
      type: Boolean,
      default: true,
    },
    showHeading: {
      type: Boolean,
      default: true,
    },
    showHistory: {
      type: Boolean,
      default: true,
    },
    showItalic: {
      type: Boolean,
      default: true,
    },
    showLink: {
      type: Boolean,
      default: true,
    },
    showParagraph: {
      type: Boolean,
      default: true,
    },
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      editor: null,
      linkUrl: null,
      linkMenuIsActive: false,
    };
  },
  computed: {
    name() {
      return this.data;
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.$props.value,
      editable: !this.$props.disabled,
      extensions: [
        new Bold(),
        new Italic(),
        new Link(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
      ],
      onUpdate: ({ getHTML }) => {
        const html = getHTML();
        this.$emit("input", this.cleanHtmlOutput(html));
      },
      onBlur: ({ state }) => {
        const html = this.editor.getHTML();

        this.$emit(
          "blur",
          this.cleanHtmlOutput(html),
          state.doc?.content?.size
        );
      },
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    cleanHtmlOutput(html) {
      const removeRegexP = /<p><\/p>/g;
      // Remove empty P tags and replace with <br>
      return html.replace(removeRegexP, "<br>");
    },
    hideLinkMenu() {
      this.linkUrl = null;
      this.linkMenuIsActive = false;
    },
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href;
      this.linkMenuIsActive = true;
      this.$nextTick(() => {
        this.$refs.linkInput.focus();
      });
    },
    setLinkUrl(command, url) {
      command({ href: url });
      this.hideLinkMenu();
      this.editor.focus();
    },
  },
};
</script>

<style scoped>
.menubar {
  @apply w-full;
  transition: visibility 0.2s 0.2s, opacity 0.2s 0.2s;

  &.is-hidden {
    visibility: hidden;
    opacity: 0;
  }

  &.is-focused {
    visibility: visible;
    opacity: 1;
  }
}

.menubar__button {
  @apply py-2 mr-2 text-xs text-grey-darker;

  &:focus {
    @apply outline-none;
  }

  &:first-child {
    @apply ml-1;
  }
}

.menububble {
  @apply absolute z-20 bg-support-dark rounded-lg shadow mb-1 invisible opacity-0 px-2 text-white text-sm;
  transition: opacity 0.1s, visibility 0.1s;

  &.is-active {
    @apply visible opacity-100;
  }
}

.menububble__button {
  @apply text-white mx-1;
}

.link-menu {
  @apply mb-4 hidden w-full;

  &.is-active {
    @apply block;
  }
}

.link-menu-form {
  @apply m-0;
}

.link-menu-button {
  @apply text-danger border-0 cursor-pointer absolute outline-none;
  font-size: 12px;
  line-height: 1;
  right: 5px;
  top: 14px;
  z-index: 10;
}

.link-menu-input {
  @apply px-2 py-1 text-sm;
}

.disabled,
.loading {
  @apply pointer-events-none;
}

.loading-icon {
  @apply absolute p-0;
  font-size: 0.6rem;
  right: 15px;
  top: 11px;
  z-index: 1;
}

.menu-icon-text {
  @apply font-normal text-xs;
}
</style>
